import {
  filter,
  find,
  forEach,
  get,
  groupBy,
  has,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
} from 'lodash';

import { DecimalNumber } from '@app/shared/common/decimal-number/decimal-number';
import {
  IBaseEntityResponse,
  IBaseEntityType,
} from '@app/shared/types/entities/base/base.type';
import {
  IInventoryItem,
  IInventoryItemVenue,
} from '@app/shared/types/entities/inventory-item-venue/inventory-item-venue.type';
import { IQohInventoryItemVenue } from '@app/shared/types/entities/transfer/transfer.type';

import {
  calculatePendingQuantity,
  getCurrentItemQoh,
} from '../transfer/transfer.util';
import { IReplenishDestination, IReplenishPercentMap } from './replenish.types';
import { TRANSFER_FORM_FIELD_PATHS } from '../transfer/transfer-form-field-paths';
import { EntityId, IEvent, ObjectId } from '@app/shared/types';
import { Threshold } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue/inventory-item-venue';
import { ThresholdType } from '@app/shared/types/enums/threshold-type.enum';
import {
  ReplenishItemSiteType,
  REPLENISH_APPLY_ITEMS_SCOPE,
} from './replenish.enums';
import { IReplenishApplyItemScopeOption } from '../transfer-replenish';
import { I18n } from '@app/i18n/strings-keys';

const isSkipReplenish = (site: {
  noAvailablePAR?: boolean;
  skipReplenish?: boolean;
}): boolean => {
  return (
    isEqual(site?.noAvailablePAR, true) || isEqual(site?.skipReplenish, true)
  );
};

const getPendingQuantityMap = (
  transferResponse: IBaseEntityResponse,
  siteId: string,
  siteIdTo: string,
): IBaseEntityType => {
  const objectFieldPath = TRANSFER_FORM_FIELD_PATHS;

  const pendingQtyMap = {};
  forEach(transferResponse.items, (transfer) => {
    forEach(transfer.inventory_items, (inventoryItem) => {
      const itemEntityId: ObjectId = inventoryItem['item_entity_id'];
      if (!has(pendingQtyMap, itemEntityId)) {
        pendingQtyMap[itemEntityId] = {
          [objectFieldPath.siteId]: new DecimalNumber(0),
          [objectFieldPath.siteIdTo]: new DecimalNumber(0),
        };
      }
      const pendingQuantity = calculatePendingQuantity(
        inventoryItem[objectFieldPath.initialCount],
        inventoryItem[objectFieldPath.measureFactorValue],
      );

      // Calculate Pending Quantities From
      // If the site_id of the transfer is equal to the siteId, is mean this item transfer to other site.
      if (isEqual(siteId, transfer[objectFieldPath.siteId])) {
        pendingQtyMap[itemEntityId][objectFieldPath.siteId] =
          pendingQtyMap[itemEntityId][objectFieldPath.siteId].sub(
            pendingQuantity,
          );
      }
      // If the site_id_to of the transfer is equal to the site_id, is mean quantity is transfer in from other site.
      if (isEqual(siteId, transfer[objectFieldPath.siteIdTo])) {
        pendingQtyMap[itemEntityId][objectFieldPath.siteId] =
          pendingQtyMap[itemEntityId][objectFieldPath.siteId].add(
            pendingQuantity,
          );
      }
      // Calculate Pending Quantities To
      // If the site_id_to of the transfer is equal to the site_id, is mean this item transfer to other site.
      if (isEqual(siteIdTo, transfer[objectFieldPath.siteId])) {
        pendingQtyMap[itemEntityId][objectFieldPath.siteIdTo] =
          pendingQtyMap[itemEntityId][objectFieldPath.siteIdTo].sub(
            pendingQuantity,
          );
      }
      // If the site_id_to of the transfer is equal to the site_id_to, is mean quantity is transfer in from other site.
      if (isEqual(siteIdTo, transfer[objectFieldPath.siteIdTo])) {
        pendingQtyMap[itemEntityId][objectFieldPath.siteIdTo] =
          pendingQtyMap[itemEntityId][objectFieldPath.siteIdTo].add(
            pendingQuantity,
          );
      }
    });
  });

  return pendingQtyMap;
};

const getQohAndPendingQuantity = (
  item: IQohInventoryItemVenue,
  path: string,
  {
    Qoh,
    PendingQty,
  }: { Qoh: IQohInventoryItemVenue[]; PendingQty: IBaseEntityType },
  siteId: string,
): string[] => {
  const qohDataForCurrentRow = getCurrentItemQoh(Qoh, siteId, item);
  const pendingQtyForCurrentRow = get(PendingQty, [siteId, item.entity_id]);

  return [
    `${get(qohDataForCurrentRow, 'count', 0)}`,
    `${get(pendingQtyForCurrentRow, path, 0)}`,
  ];
};

const takeIntersectionAndProcessThreshold = (
  inventoryItems: IInventoryItem[],
  destination: IReplenishDestination,
): IQohInventoryItemVenue[] => {
  const buildGroupItemKey = (
    entityId: EntityId,
    siteId: EntityId,
    siteType: ReplenishItemSiteType,
  ) => `${entityId}_${siteId}_${siteType}`;

  const groupAllInventoryItems = groupBy(inventoryItems, (item) => {
    return buildGroupItemKey(item.entity_id, item.site_id, item.siteType);
  });

  return filter(inventoryItems, (item: any) => {
    const fromSiteItems = get(
      groupAllInventoryItems,
      buildGroupItemKey(
        item.entity_id,
        destination.fromSite,
        ReplenishItemSiteType.From,
      ),
    );

    const toSiteItems = get(
      groupAllInventoryItems,
      buildGroupItemKey(
        item.entity_id,
        destination.toSite,
        ReplenishItemSiteType.To,
      ),
    );

    return (
      isEqual(item.siteType, ReplenishItemSiteType.To) &&
      isEqual(item.site_id, destination.toSite) &&
      !isEmpty(fromSiteItems) &&
      !isEmpty(toSiteItems)
    );
  }) as IQohInventoryItemVenue[];
};

/**
 * @param item Inventory Item
 * @param selectedEvent
 * logic:
 * - for outside: will use default threshold,
 * - for event: will use selected event event_type_entity_id to find event_type threshold or null
 */
const getItemThreshold = (
  item: IQohInventoryItemVenue,
  selectedEvent?: IEvent,
): Threshold => {
  const thresholds: Threshold[] = get(item, 'thresholds', []);
  let threshold: Threshold;

  const isOutsideOfEventTransfer = isEmpty(selectedEvent);

  if (isOutsideOfEventTransfer) {
    threshold = find(thresholds, { type: ThresholdType.Default });
  } else {
    threshold = selectedEvent
      ? find(thresholds, {
          type: ThresholdType.EventType,
          event_type_entity_id: selectedEvent.event_type_entity_id,
        })
      : null;
  }

  return threshold || null;
};

const dispatchCallbackEvent = <T = any, R = any>(
  eventTarget: any,
  eventPath: string | string[],
  params: T,
): R => {
  const event = get(eventTarget, eventPath);

  if (!isFunction(event)) {
    return;
  }

  return event(params);
};

const getHasInitialCountItems = (
  items: (IInventoryItemVenue | IQohInventoryItemVenue)[],
): (IInventoryItemVenue | IQohInventoryItemVenue)[] =>
  filter(
    items,
    (inventoryItem) =>
      !isNil(inventoryItem[TRANSFER_FORM_FIELD_PATHS.initialCount]),
  );

const buildUniqSites = (
  percentDesMap: IReplenishPercentMap,
  selectFiledPath: 'fromSite' | 'toSite',
): EntityId[] => {
  const siteSet = new Set<EntityId>();

  forEach(percentDesMap, (dest) => {
    siteSet.add(dest[selectFiledPath]);
  });

  return Array.from(siteSet);
};

const getReplenishItemScopeOptions = (
  filtered: boolean = false,
): IReplenishApplyItemScopeOption[] => {
  return [
    {
      text: I18n.Forms.GridForm.ChargeableOnly,
      value: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
    },
    {
      text: I18n.Forms.GridForm.NonChargeableOnly,
      value: REPLENISH_APPLY_ITEMS_SCOPE.NON_CHARGEABLE_ONLY,
    },
    {
      text: filtered
        ? I18n.Forms.GridForm.AllFilteredItems
        : I18n.Forms.GridForm.AllItems,
      value: REPLENISH_APPLY_ITEMS_SCOPE.ALL_ITEMS,
    },
  ];
};

export {
  takeIntersectionAndProcessThreshold,
  isSkipReplenish,
  getPendingQuantityMap,
  getQohAndPendingQuantity,
  dispatchCallbackEvent,
  getHasInitialCountItems,
  buildUniqSites,
  getItemThreshold,
  getReplenishItemScopeOptions,
};
